<script setup>
const fullWidthMode = inject("fullWidthMode", false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
import NcodeLetters from '@/components/saju-insight/common/NcodeLetters.vue';
import ContentsTitle from './ContentsTitle.vue';
import { transformYearlyFortuneText } from '@/composables/splitSentence';

const props = defineProps({
  userName: {
    type: String,
    default: '',
  },
  isMobile: {
    type: Boolean,
    default: false,
  },
  data: {
    type: Object,
    default: () => ({}),
  },
});

// 데이터 접근
const 에너지방향 = computed(() => props.data?.에너지방향 || {});
const 근원 = computed(() => 에너지방향.value?.근원 || {});
const 요즘 = computed(() => 에너지방향.value?.요즘 || {});

// 디버깅용 로그
watch(
  () => props.data,
  (newData) => {
    console.log('Contents06 props.data:', newData);
    console.log('에너지방향:', newData?.에너지방향);
  },
  { immediate: true, deep: true },
);

// NCODE 코드 (예: "ISTJ+G")
const 근원코드 = computed(() => 근원.value?.코드 || 근원.value?.NCODE || '');
const 요즘코드 = computed(() => 요즘.value?.코드 || 요즘.value?.NCODE || '');

// MBTI만 추출 ("+G" 제외)
const getMBTIOnly = (code) => {
  if (!code) return '';
  return code.split('+')[0] || '';
};
const 근원MBTI = computed(() => getMBTIOnly(근원코드.value));
const 요즘MBTI = computed(() => getMBTIOnly(요즘코드.value));

// 오른쪽 영역 데이터
const 요즘성향 = computed(() => 에너지방향.value?.요즘성향 || {});
const 기준정보 = computed(() => 요즘성향.value?.기준정보 || {});
const 개념특징 = computed(() => 요즘성향.value?.개념특징 || {});

const 요즘키워드 = computed(() => {
  const raw = 기준정보.value?.특징요약 || '';
  return raw ? raw.split(', ') : [];
});
const 요즘개념 = computed(() => {
  return transformYearlyFortuneText(개념특징.value?.개념 || '');
});
const 요즘특징 = computed(() => {
  const raw = 개념특징.value?.특징 || '';
  return raw ? raw.split('|') : [];
});
const 발전포인트 = computed(() => {
  const raw = 개념특징.value?.강점요약 || '';
  return raw ? raw.split(', ') : [];
});
const 체크포인트 = computed(() => {
  const raw = 개념특징.value?.약점요약 || '';
  return raw ? raw.split(', ') : [];
});

// 성향요약에서 별명 파싱: "ISTJ(현실적 관리자) → ISFP(유연한 예술가)"
const 성향요약 = computed(() => 에너지방향.value?.성향요약 || '');
const 근원유형 = computed(() => {
  const match = 성향요약.value.match(/\(([^)]+)\)\s*→/);
  return match ? match[1] : '';
});
const 요즘유형 = computed(() => {
  const match = 성향요약.value.match(/→\s*\w+\(([^)]+)\)/);
  return match ? match[1] : '';
});

const 비교설명 = computed(() => transformYearlyFortuneText(에너지방향.value?.성향변화 || ''));
const 에너지상태 = computed(() => transformYearlyFortuneText(에너지방향.value?.용신변화 || ''));
const 요약 = computed(() => transformYearlyFortuneText(에너지방향.value?.종합평가 || ''));

// 바 차트 데이터 계산
// 데이터 형식: { EI: { type: "I", value: 53.41 }, NS: { type: "S", value: 70 }, ... }
const getNCodeChartData = (rawData) => {
  if (!rawData) return null;

  const pairs = [
    { key: 'EI', left: 'E', right: 'I' },
    { key: 'NS', left: 'S', right: 'N' },
    { key: 'FT', left: 'T', right: 'F' },
    { key: 'PJ', left: 'J', right: 'P' },
  ];

  const result = [];

  for (const pair of pairs) {
    const data = rawData[pair.key];
    if (data && data.type !== undefined && data.value !== undefined) {
      // { type: "I", value: 53.41 } 형식
      const dominant = data.type;
      const dominantValue = data.value;
      const otherValue = 100 - dominantValue;

      const leftValue = dominant === pair.left ? dominantValue : otherValue;
      const rightValue = dominant === pair.right ? dominantValue : otherValue;

      result.push({
        left: pair.left,
        right: pair.right,
        leftValue: leftValue,
        rightValue: rightValue,
        leftActive: leftValue > rightValue,
        rightActive: rightValue > leftValue,
      });
    } else {
      // 기본값
      result.push({
        left: pair.left,
        right: pair.right,
        leftValue: 50,
        rightValue: 50,
        leftActive: false,
        rightActive: false,
      });
    }
  }

  return result;
};

const 근원차트데이터 = computed(() => getNCodeChartData(근원.value));
const 요즘차트데이터 = computed(() => getNCodeChartData(요즘.value));

// 색상 매핑
const colors = {
  E: '#ff6867',
  I: '#4eaeea',
  S: '#7d67fd',
  N: '#adcc32',
  T: '#27b190',
  F: '#e53c9c',
  J: '#4881ce',
  P: '#ffb401',
};

const getBarColor = (letter, isActive) => {
  return isActive ? colors[letter] : '#ebebeb';
};

const getTextColor = (isActive) => {
  return isActive ? '#fff' : '#757575';
};
</script>

<template>
  <VRow class="full-height over-hidden">
    <!-- 왼쪽 컬럼 -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="personality-card full-height right">
        <!-- 타이틀 -->
        <ContentsTitle
          :number="4"
          text="2026년 나는 어떤 성향의 흐름을 가질까요?"
          :isMobile="props.isMobile"
        />

        <div class="tendency-wrapper" :class="{ mobile: props.isMobile }">
          <!-- 기본 성향 섹션 -->
          <div class="tendency-section">
            <!-- NCODE 카드 -->
            <div class="tendency-ncode-card">
              <span class="ncode-label">타고난 나의 기본 성향</span>
              <NcodeLetters :myNcode="근원코드" />
            </div>

            <!-- 바 차트 -->
            <div class="tendency-chart" v-if="근원차트데이터">
              <!-- 상단 숫자 레이블 -->
              <div class="chart-labels">
                <span>100</span>
                <span>75</span>
                <span>50</span>
                <span>25</span>
                <span class="center">0</span>
                <span>25</span>
                <span>50</span>
                <span>75</span>
                <span>100</span>
              </div>

              <!-- 바 영역 -->
              <div class="chart-bars">
                <!-- 세로선 -->
                <div class="chart-grid">
                  <div class="grid-line dashed"></div>
                  <div class="grid-line dashed"></div>
                  <div class="grid-line dashed"></div>
                  <div class="grid-line dashed"></div>
                  <div class="grid-line center-line"></div>
                  <div class="grid-line dashed"></div>
                  <div class="grid-line dashed"></div>
                  <div class="grid-line dashed"></div>
                  <div class="grid-line dashed"></div>
                </div>

                <!-- 바 아이템들 -->
                <div v-for="(item, idx) in 근원차트데이터" :key="`origin-${idx}`" class="bar-row">
                  <!-- 왼쪽 바 -->
                  <div class="bar-left">
                    <div
                      class="bar"
                      :class="{ 'bar--active': item.leftActive }"
                      :style="{
                        width: `${item.leftValue}%`,
                        backgroundColor: getBarColor(item.left, item.leftActive),
                      }"
                    >
                      <span class="bar-letter" :style="{ color: getTextColor(item.leftActive) }">{{
                        item.left
                      }}</span>
                      <span class="bar-tooltip">{{ item.leftValue.toFixed(1) }}%</span>
                    </div>
                  </div>

                  <!-- 오른쪽 바 -->
                  <div class="bar-right">
                    <div
                      class="bar"
                      :class="{ 'bar--active': item.rightActive }"
                      :style="{
                        width: `${item.rightValue}%`,
                        backgroundColor: getBarColor(item.right, item.rightActive),
                      }"
                    >
                      <span class="bar-letter" :style="{ color: getTextColor(item.rightActive) }">{{
                        item.right
                      }}</span>
                      <span class="bar-tooltip">{{ item.rightValue.toFixed(1) }}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 현재 성향 섹션 -->
          <div class="tendency-section">
            <!-- NCODE 카드 -->
            <div class="tendency-ncode-card">
              <span class="ncode-label two-line">
                <span>2026년에 드러나는</span>
                <span>현재의 성향</span>
              </span>
              <NcodeLetters :myNcode="요즘코드" />
            </div>

            <!-- 바 차트 -->
            <div class="tendency-chart" v-if="요즘차트데이터">
              <!-- 상단 숫자 레이블 -->
              <div class="chart-labels">
                <span>100</span>
                <span>75</span>
                <span>50</span>
                <span>25</span>
                <span class="center">0</span>
                <span>25</span>
                <span>50</span>
                <span>75</span>
                <span>100</span>
              </div>

              <!-- 바 영역 -->
              <div class="chart-bars">
                <!-- 세로선 -->
                <div class="chart-grid">
                  <div class="grid-line dashed"></div>
                  <div class="grid-line dashed"></div>
                  <div class="grid-line dashed"></div>
                  <div class="grid-line dashed"></div>
                  <div class="grid-line center-line"></div>
                  <div class="grid-line dashed"></div>
                  <div class="grid-line dashed"></div>
                  <div class="grid-line dashed"></div>
                  <div class="grid-line dashed"></div>
                </div>

                <!-- 바 아이템들 -->
                <div v-for="(item, idx) in 요즘차트데이터" :key="`current-${idx}`" class="bar-row">
                  <!-- 왼쪽 바 -->
                  <div class="bar-left">
                    <div
                      class="bar"
                      :class="{ 'bar--active': item.leftActive }"
                      :style="{
                        width: `${item.leftValue}%`,
                        backgroundColor: getBarColor(item.left, item.leftActive),
                      }"
                    >
                      <span class="bar-letter" :style="{ color: getTextColor(item.leftActive) }">{{
                        item.left
                      }}</span>
                      <span class="bar-tooltip">{{ item.leftValue.toFixed(1) }}%</span>
                    </div>
                  </div>

                  <!-- 오른쪽 바 -->
                  <div class="bar-right">
                    <div
                      class="bar"
                      :class="{ 'bar--active': item.rightActive }"
                      :style="{
                        width: `${item.rightValue}%`,
                        backgroundColor: getBarColor(item.right, item.rightActive),
                      }"
                    >
                      <span class="bar-letter" :style="{ color: getTextColor(item.rightActive) }">{{
                        item.right
                      }}</span>
                      <span class="bar-tooltip">{{ item.rightValue.toFixed(1) }}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>

    <!-- 오른쪽 컬럼 -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="personality-card full-height">
        <div class="right-wrapper" :class="{ mobile: props.isMobile }">
          <!-- 섹션1: 지금 나에게 나타나는 성향의 모습 -->
          <div class="right-section">
            <div class="section-title">🌷 지금 나에게 나타나는 성향의 모습</div>

            <!-- MBTI + 키워드 -->
            <div class="mbti-keyword-row">
              <div class="mbti-box">
                <div class="mbti-chars">
                  <span
                    v-for="(char, idx) in 요즘MBTI.split('')"
                    :key="idx"
                    class="mbti-char"
                    :style="{ color: colors[char] }"
                    >{{ char }}</span
                  >
                </div>
              </div>
              <div class="keyword-tags blue">
                <span v-for="(kw, idx) in 요즘키워드" :key="idx" class="tag">{{ kw }}</span>
              </div>
            </div>

            <!-- 개념 -->
            <div class="concept-box">
              <div class="concept-title">개념</div>
              <div class="concept-text" v-html="요즘개념"></div>
            </div>

            <div class="divider"></div>

            <!-- 특징들 -->
            <div class="feature-box">
              <div class="feature-title">내가 나타낼 수 있는 특징들</div>
              <ul class="feature-list">
                <li
                  v-for="(item, idx) in 요즘특징"
                  :key="idx"
                  v-html="transformYearlyFortuneText(item)"
                ></li>
              </ul>
            </div>

            <!-- 발전/체크 포인트 -->
            <div class="point-row">
              <div class="point-card green">
                <div class="point-title">발전 포인트</div>
                <div class="keyword-tags green">
                  <span v-for="(kw, idx) in 발전포인트" :key="idx" class="tag">{{ kw }}</span>
                </div>
              </div>
              <div class="point-card red">
                <div class="point-title">체크 포인트</div>
                <div class="keyword-tags red">
                  <span v-for="(kw, idx) in 체크포인트" :key="idx" class="tag">{{ kw }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="divider"></div>

          <!-- 섹션2: 성향의 현재 위치 -->
          <div class="right-section position-section">
            <div class="section-title">📍 성향의 현재 위치</div>
            <div class="position-compare">
              <div class="position-card">
                <div class="mbti-chars">
                  <span
                    v-for="(char, idx) in 근원MBTI.split('')"
                    :key="`from-${idx}`"
                    class="mbti-char"
                    :style="{ color: colors[char] }"
                    >{{ char }}</span
                  >
                </div>
                <span class="position-name">{{ 근원유형 }}</span>
              </div>
              <div class="arrow-icon">→</div>
              <div class="position-card">
                <div class="mbti-chars">
                  <span
                    v-for="(char, idx) in 요즘MBTI.split('')"
                    :key="`to-${idx}`"
                    class="mbti-char"
                    :style="{ color: colors[char] }"
                    >{{ char }}</span
                  >
                </div>
                <span class="position-name">{{ 요즘유형 }}</span>
              </div>
            </div>
            <div class="compare-text" v-html="비교설명"></div>
          </div>

          <!-- 섹션3: 성향을 뒷받침하는 에너지의 상태 -->
          <template v-if="에너지상태">
            <div class="divider"></div>
            <div class="right-section">
              <div class="section-title">⚙ 성향을 뒷받침하는 에너지의 상태</div>
              <div class="energy-text" v-html="에너지상태"></div>
            </div>
          </template>

          <!-- 하단 요약 카드 -->
          <div class="summary-card" v-if="요약">
            <div class="summary-text" v-html="요약"></div>
          </div>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>

<style scoped lang="scss">
.tendency-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 40px;
  padding: 24px;
  padding-bottom: 130px;
  overflow-y: auto;
  background: #fff;

  &.mobile {
    padding: 20px 16px;
    padding-bottom: 24px;
    gap: 24px;

    .tendency-section {
      padding-bottom: 24px;
      border-bottom: 1px solid #ebebeb;

      &:last-child {
        padding-bottom: 0;
        border-bottom: none;
      }
    }

    .tendency-ncode-card {
      justify-content: space-between;
      gap: 20px;
      padding: 16px 20px;
    }

    .tendency-chart {
      overflow-x: auto;
    }

    .chart-bars {
      min-width: 350px;
    }
  }
}

.tendency-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
}

.tendency-ncode-card {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 40px;
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #f8f8f8;
  background: #fff;
  box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.1);

  .ncode-label {
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;

    &.two-line {
      display: flex;
      flex-direction: column;
      text-align: center;
    }
  }
}

.tendency-chart {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: 552px;
}

.chart-labels {
  display: flex;
  justify-content: space-between;
  padding: 0 6px;

  span {
    width: 20px;
    color: #333;
    font-family: Pretendard;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    text-align: center;

    &.center {
      font-weight: 700;
    }
  }
}

.chart-bars {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 28px;
  padding: 18px 0;
}

.chart-grid {
  position: absolute;
  top: 0;
  left: 6px;
  right: 6px;
  bottom: 0;
  display: flex;
  justify-content: space-between;
  pointer-events: none;

  .grid-line {
    width: 1px;
    height: 100%;

    &.dashed {
      border-left: 1px dashed #ebebeb;
    }

    &.center-line {
      z-index: 10;
      background: #c2c2c2;
    }
  }
}

.bar-row {
  display: flex;
  height: 20px;
  position: relative;
  z-index: 1;

  &:nth-child(1) .bar {
    animation-delay: 0.05s;
  }
  &:nth-child(2) .bar {
    animation-delay: 0.12s;
  }
  &:nth-child(3) .bar {
    animation-delay: 0.1s;
  }
  &:nth-child(4) .bar {
    animation-delay: 0.2s;
  }
}

@keyframes barGrowLeft {
  0% {
    transform: scaleX(0);
  }
  70% {
    transform: scaleX(1.08);
  }
  85% {
    transform: scaleX(0.97);
  }
  100% {
    transform: scaleX(1);
  }
}

@keyframes barGrowRight {
  0% {
    transform: scaleX(0);
  }
  70% {
    transform: scaleX(1.08);
  }
  85% {
    transform: scaleX(0.97);
  }
  100% {
    transform: scaleX(1);
  }
}

.bar-left {
  flex: 1;
  display: flex;
  justify-content: flex-end;
  padding-right: 0;

  .bar {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding-left: 8px;
    height: 100%;
    border-radius: 8px 0 0 8px;
    min-width: 26px;
    transform-origin: right center;
    animation: barGrowLeft 0.6s ease-out backwards;
  }
}

.bar-right {
  flex: 1;
  display: flex;
  justify-content: flex-start;
  padding-left: 0;

  .bar {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding-right: 8px;
    height: 100%;
    border-radius: 0 8px 8px 0;
    min-width: 26px;
    transform-origin: left center;
    animation: barGrowRight 0.6s ease-out backwards;
  }
}

.bar-letter {
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
}

.bar-tooltip {
  position: absolute;
  top: -28px;
  padding: 2px 6px;
  border-radius: 4px;
  background: #333;
  color: #fff;
  font-family: Pretendard;
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
}

.bar-left .bar-tooltip {
  left: 0;
}

.bar-right .bar-tooltip {
  right: 0;
}

.bar--active:hover {
  filter: brightness(1.12);
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.15);
}

.bar--active:hover .bar-tooltip {
  opacity: 1;
}

// ========== 오른쪽 영역 스타일 ==========
.right-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  padding-bottom: 130px;
  overflow-y: auto;
  background: #fff;

  &.mobile {
    padding: 20px 16px;
    padding-bottom: 24px;

    .position-compare {
      flex-direction: column;
      gap: 12px;
      height: auto;
    }

    .position-card {
      flex: unset;
      padding: 12px 40px;
    }

    .arrow-icon {
      transform: rotate(90deg);
    }
  }
}

.right-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-title {
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
}

.divider {
  width: 100%;
  height: 1px;
  background: #ebebeb;
}

.mbti-keyword-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 30px;
}

.mbti-box {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 40px;
  border: 1px solid #ebebeb;
  border-radius: 9px;
  background: #fff;
}

.keyword-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  .tag {
    padding: 3px 6px;
    border-radius: 4px;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 700;
    line-height: 18px;
  }

  &.blue .tag {
    background: rgba(87, 144, 252, 0.07);
    color: #5790fc;
  }

  &.green .tag {
    background: rgba(136, 195, 74, 0.07);
    color: #88c34a;
  }

  &.red .tag {
    background: rgba(255, 110, 110, 0.07);
    color: #ff6e6e;
  }
}

.concept-box {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.concept-title {
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
}

.concept-text {
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 400;
  line-height: 24px;
}

.feature-box {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.feature-title {
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
}

.feature-list {
  margin: 0;
  padding-left: 16px;
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 400;
  line-height: 24px;
  list-style-type: none;

  li {
    margin-bottom: 4px;
    position: relative;
    padding-left: 12px;

    &::before {
      content: '•';
      position: absolute;
      left: 0;
      font-size: 10px;
      line-height: 24px;
    }
  }
}

.point-row {
  display: flex;
  gap: 16px;
}

.point-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.8);

  &.green {
    border: 1px solid #88c34a;
  }

  &.red {
    border: 1px solid #ff6e6e;
  }
}

.point-title {
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
}

.position-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.8);

  .section-title {
    width: 100%;
  }
}

.position-compare {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.position-card {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 12px 20px;
  border: 1px solid #ebebeb;
  border-radius: 9px;
  background: #fff;
}

.mbti-chars {
  display: flex;
  align-items: center;
  gap: 4px;

  .mbti-char {
    font-family: Pretendard;
    font-size: 18px;
    font-weight: 700;
    line-height: 32px;
  }
}

.position-name {
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 400;
  line-height: 24px;
}

.arrow-icon {
  color: #333;
  font-size: 24px;
  line-height: 1;
}

.compare-text,
.energy-text {
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 400;
  line-height: 24px;
}

.summary-card {
  padding: 16px;
  border-radius: 12px;
  background: rgba(87, 144, 252, 0.05);
}

.summary-text {
  color: #5790fc;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
  text-align: center;
}
</style>
